import { defineStore } from 'pinia';
import { connection } from '@vp/vue-components';
import {
  type CompleteOrderInput,
  type CreatePaymentInformationInput,
  type Mutation,
  type Query, type RecoverOrderInput, type SaveOrderInput,
  ShoppingCartItemTypeEnum, type UpdatePaymentInformationInput,
} from '@vp/common/generated/checkout.graphql';
import { Checkout, Util } from '@vp/common';
import { VPErrors } from '@vp/common';
import * as VPErrorLegacy from '../types/VPError';
import { DateTime } from 'luxon';
import { ApolloError, gql } from '@apollo/client/core';
import { NotFoundError } from '@vp/common/errors';
import { useCookies } from '@vueuse/integrations/useCookies';
import * as Sentry from '@sentry/vue';
import { useLanguageStore } from '../languageStore';
import { SHOPPING_CART_TOKEN_COOKIE_KEY } from '../constants';
import { until } from '@vueuse/core';

const { Arrays, LoadingState } = Util;
type LoadingState = Util.LoadingState;

const {
  OrderSummary: CheckoutOrderSummary,
  TicketBlueprint: CheckoutTicketBlueprint,
  ShoppingCartItemInput,
} = Checkout;

const { useConnectionStore } = connection;

const SHOPPING_CART_FRAGMENT = Checkout.Api.SHOPPING_CART_FRAGMENT;

export class TicketLockingError extends Error {}

export interface TicketBlueprint {
  id: number;
  name: string;
  description?: string;
  price: number;
}

interface CheckoutState {
  _selectedEventId?: number | string;
  selectedEvent?: Checkout.CheckoutEvent.CheckoutEvent;
  /** @deprecated Use `selectedEventLoadingState` */
  selectedEventLoading: boolean;
  selectedEventLoadingState: LoadingState;
  ticketsLoading: LoadingState;
  tickets: Checkout.TicketBlueprint.CheckoutTicketBlueprint[];
  shoppingCartToken?: string;
  shoppingCart: Checkout.ShoppingCart.ShoppingCart | null;
  _cartGeneration: number;
  /** Object labels whose seats.io hold is in flight; gates advancing to payment. */
  pendingHolds: Set<string>;
  /**
   * A cart write was refused because the presale needs a password. Raised by any writer and
   * rendered by Checkout.vue, which is two levels above the seating picker — EventDetails
   * renders that behind clientOnly(), so neither a template ref nor an emit reaches it.
   */
  presalePasswordRequired: boolean;
  _shoppingCartLoading: boolean;
  shoppingCartLoadingState: LoadingState;
  shoppingCartLockLoading: LoadingState;
  orderSummary?: Checkout.OrderSummary.OrderSummary;
  orderSummaryLoading?: LoadingState;
}

const GET_AVAILABLE_TICKETS_QUERY = gql`
  query GetAvailableTickets($shoppingCartToken: String) {
    availableTickets(shoppingCartToken: $shoppingCartToken) {
      id
      eventId
      name
      ticketType
      description
      soldOut
      category
      categoryId
      minimum
      maximum
      multiple
      breakdown {
        price
        displayPrice
        convenienceFee
        taxFee
        ccFee
      }
    }
  }
`;

const GET_ORDER_SUMMARY = gql`
  query GetOrderSummary($fingerprint: String!) {
    orderSummary(fingerprint: $fingerprint) {
      ...OrderSummaryFields
    }
  }
  ${Checkout.Api.ORDER_SUMMARY_FIELDS}
`;

const GET_ORDER_SUMMARY_PRINT_TICKETS = gql`
  query GetOrderSummary($fingerprint: String!) {
    orderSummary(fingerprint: $fingerprint) {
      ...OrderSummaryFields
    }
  }
  ${Checkout.Api.ORDER_SUMMARY_PRINT_TICKET_FIELDS}
`;

const getStoredCartToken = (): string | undefined => {
  if (!import.meta.env.SSR) {
    const cookies = useCookies();
    return cookies.get(SHOPPING_CART_TOKEN_COOKIE_KEY);
  }

  return undefined;
};

type ItemBreakdown = NonNullable<Checkout.ShoppingCartItem.ShoppingCartItem['breakdown']>;

// Left undefined when no item has one: trackingItems sends `basePrice` to GA4, so a zero would read as a free ticket.
const mergeBreakdown = (acc: ItemBreakdown | undefined, next: ItemBreakdown | undefined): ItemBreakdown | undefined => {
  if (!next) {
    return acc;
  }

  return {
    // This represents the price for 1 piece
    basePrice: next.basePrice,
    price: (acc?.price ?? 0) + next.price,
    total: (acc?.total ?? 0) + next.total,
  };
};

const storeCartToken = (token: string | null) => {
  if (import.meta.env.SSR) {
    return;
  }

  const cookies = useCookies();
  if (token) {
    // Partitioned (CHIPS) so the cart survives a cross-site iframe; see
    // packages/checkout/doc/claude/iframe-test-results.md. Path moves from
    // '/e' to '/' because partitioning makes it load-bearing, and because the
    // narrower scope would have broken once we serve more than one event.
    cookies.remove(SHOPPING_CART_TOKEN_COOKIE_KEY, { path: '/e' });
    cookies.set(SHOPPING_CART_TOKEN_COOKIE_KEY, token, {
      path: '/',
      secure: true,
      sameSite: 'none',
      partitioned: true,
    });
  } else {
    cookies.remove(SHOPPING_CART_TOKEN_COOKIE_KEY, { path: '/' });
    cookies.remove(SHOPPING_CART_TOKEN_COOKIE_KEY, { path: '/e' });
  }
};

type OrderSummaryReponse = {
  orderSummary?: Checkout.OrderSummary.OrderSummary;
  errors?: VPErrors.VPError[];
  errorCode?: string;
};

type RecoverOrderResponse = {
  success: boolean;
  errorCode?: string;
  orderSummary?: Checkout.OrderSummary.OrderSummary;
  errors?: VPErrors.VPError[];
};

export const useCheckoutStore = defineStore('checkout', {
  state: (): CheckoutState => ({
    selectedEventLoading: false,
    selectedEventLoadingState: LoadingState.UNKNOWN,
    selectedEvent: undefined,
    ticketsLoading: LoadingState.UNKNOWN,
    tickets: [],
    shoppingCart: Checkout.ShoppingCart.getNew(),
    shoppingCartToken: getStoredCartToken(),
    _cartGeneration: 0,
    pendingHolds: new Set(),
    presalePasswordRequired: false,
    _shoppingCartLoading: false,
    shoppingCartLoadingState: LoadingState.UNKNOWN,
    shoppingCartLockLoading: LoadingState.UNKNOWN,
    orderSummaryLoading: LoadingState.UNKNOWN,
  }),
  getters: {
    initialized(state: CheckoutState): boolean {
      return !state.selectedEventLoading && !!state.selectedEvent;
    },

    getSelectedEventId(state: CheckoutState): number | string | undefined {
      if (state._selectedEventId) {
        return state._selectedEventId;
      }

      // if (!import.meta.env.SSR) {
      //   const rootStore = useRootStore();
      //   return <string>rootStore.route?.params?.eventId;
      // }
    },

    getSelectedEvent(state: CheckoutState): Checkout.CheckoutEvent.CheckoutEvent | undefined {
      return state.orderSummary?.event || state.selectedEvent;
    },

    selectedEventTickets(state: CheckoutState): Checkout.TicketBlueprint.CheckoutTicketBlueprint[] {
      return state.tickets.filter(({ eventId }) => this.getSelectedEvent?.id == eventId);
    },

    availableTickets(): Checkout.TicketBlueprint.CheckoutTicketBlueprint[] {
      return this.selectedEventTickets.filter(({ soldOut }) => !soldOut);
    },

    getCartedTickets(state: CheckoutState): Checkout.ShoppingCartItem.ShoppingCartItem[] {
      if (!state.shoppingCart) {
        return [];
      }

      const tickets = state.shoppingCart.items.filter(
        ({ itemType }) =>
          itemType === ShoppingCartItemTypeEnum.Ticket || itemType === ShoppingCartItemTypeEnum.SeatTicket,
      );

      const grouped = Arrays.groupBy(tickets, 'typeHash');
      return Object.keys(grouped).reduce((acc, key): Checkout.ShoppingCartItem.ShoppingCartItem[] => {
        const ticketsInGroup = grouped[key];
        const summary = ticketsInGroup.reduce(
          (ticketAcc, ticket): Checkout.ShoppingCartItem.ShoppingCartItem => {
            return {
              ...ticket,
              count: (ticketAcc.count ?? 0) + (ticket.count ?? 0),
              breakdown: mergeBreakdown(ticketAcc.breakdown, ticket.breakdown),
            };
          },
          { count: 0 } as Checkout.ShoppingCartItem.ShoppingCartItem,
        );

        return [...acc, summary];
      }, [] as Checkout.ShoppingCartItem.ShoppingCartItem[]);
    },

    getCartedSeatTickets(state: CheckoutState): Checkout.ShoppingCartItem.ShoppingCartItem[] {
      if (!state.shoppingCart) {
        return [];
      }

      return state.shoppingCart.items.filter(({ itemType }) => itemType === ShoppingCartItemTypeEnum.SeatTicket);
    },

    getEventCheckoutConfig(state: CheckoutState): Checkout.CheckoutConfig.CheckoutConfig | undefined {
      return state.selectedEvent?.config;
    },

    getProcessorPublicKey(): string | undefined {
      return this.getEventCheckoutConfig?.processorPublicKey;
    },

    hasValidLock(state: CheckoutState): boolean {
      if (!state.shoppingCart?.lockExpiresAt) {
        return false;
      }
      return DateTime.fromISO(state.shoppingCart.lockExpiresAt) > DateTime.now();
    },

    seatsioHoldToken(state: CheckoutState): string | undefined {
      return state.shoppingCart?.seatsioHoldToken;
    },

    holdsSettling(state: CheckoutState): boolean {
      return state.pendingHolds.size > 0;
    },
  },
  actions: {
    async init() {
      await this.fetchSelectedEvent();

      // Before the queries below, so they carry the venue's locale as
      // X-VP-Locale when the buyer arrived without `?lang=`.
      try {
        const languageStore = useLanguageStore();
        await languageStore.setLanguage(this.selectedEvent?.venue?.locale);
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
      }

      await this.loadShoppingCart();

      await this.fetchAvailableTickets();
    },

    // Backend copy is translated off X-VP-Locale at fetch time; doc/claude/i18n.md scopes this.
    async changeLanguage(locale: string) {
      const languageStore = useLanguageStore();

      try {
        await languageStore.setLanguage(locale, true);

        if (!this.getSelectedEventId) {
          return;
        }

        // A read in flight went out under the old locale: wait it out rather than race it.
        await until(() => this.selectedEventLoading).toBe(false, { timeout: 5000, throwOnTimeout: false });

        await this.fetchSelectedEvent({ force: true });
      } catch (error) {
        Sentry.captureException(error);
      }
    },

    // `reset()` is the wrong tool once an order exists: its `init()` reserves a fresh cart.
    discardCart() {
      this._cartGeneration++;
      this._shoppingCartLoading = false;
      this.setShoppingCartToken(null);
      this.shoppingCart = null;
      this.shoppingCartLoadingState = LoadingState.UNKNOWN;
      this.tickets = [];
      this.ticketsLoading = LoadingState.UNKNOWN;
    },

    async reset() {
      try {
        await this.releaseLock();
      } catch (e) {
        // The lock lapses server-side on its own; a failed release must not leave the
        // dead cart in the browser to be picked up — and expire again — on the next load.
        Sentry.captureException(e);
      }

      this.discardCart();

      this.selectedEvent = undefined;
      this.selectedEventLoadingState = LoadingState.UNKNOWN;
      this.orderSummary = undefined;
      await this.init();
    },

    setShoppingCartToken(token: string | null) {
      storeCartToken(token);
      this.shoppingCartToken = token ?? undefined;
    },

    async fetchSelectedEvent({ force = false } = {}) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      const selectedEventId = this.getSelectedEventId;

      if (this.getSelectedEvent && !force) {
        return;
      }

      if (!selectedEventId) {
        throw 'No selected event id.';
      }

      if (this.selectedEventLoading && !force) {
        // TODO: wait until finished loading and check if the required event is loaded
        return;
      }

      this.selectedEventLoadingState = LoadingState.LOADING;
      this.selectedEventLoading = true;
      try {
        this.selectedEvent = await Checkout.Api.getEvent(apiClient, selectedEventId);
        this.selectedEventLoadingState = LoadingState.DONE;
      } catch (e) {
        this.selectedEventLoadingState = LoadingState.ERROR;

        // instanceof doesn't work correctly in vike SSR
        if (typeof e === 'object' && e !== null && e.constructor.name === 'ApolloError') {
          const error: ApolloError = <ApolloError>e;
          const extensions = error.cause?.extensions;
          if (!Array.isArray(extensions) && extensions?.code == 'NOT_FOUND') {
            this.selectedEventLoadingState = LoadingState.NOT_FOUND;
            throw new NotFoundError();
          }
        }
        throw e;
      } finally {
        this.selectedEventLoading = false;
      }
    },

    async fetchAvailableTickets({ force = false } = {}) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      if (this.ticketsLoading === LoadingState.LOADING) {
        return;
      }

      if (this.ticketsLoading == LoadingState.DONE && !force) {
        return;
      }

      this.ticketsLoading = LoadingState.LOADING;
      try {
        const { data } = await apiClient.query<Query>({
          query: GET_AVAILABLE_TICKETS_QUERY,
          variables: {
            shoppingCartToken: this.shoppingCartToken,
          },
          fetchPolicy: 'network-only',
        });

        if (!data.availableTickets) {
          this.ticketsLoading = LoadingState.ERROR;
          return;
        }

        this.tickets = data.availableTickets.map(CheckoutTicketBlueprint.fromGql);
        this.ticketsLoading = LoadingState.DONE;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        this.ticketsLoading = LoadingState.ERROR;
      }
    },

    async loadShoppingCart() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      if (this._shoppingCartLoading || this.shoppingCartLoadingState === LoadingState.LOADING) {
        // TODO: wait until finished loading and check if the required event is loaded
        return;
      }

      if (this.shoppingCartLoadingState === LoadingState.DONE) {
        // Already loaded — but possibly during SSR, where storeCartToken is a
        // no-op, and the hydrated DONE state then short-circuits us here on the
        // client. Without re-asserting, the token is never persisted at all on
        // the ssr:true tickets page and a reload starts an empty cart.
        if (this.shoppingCartToken) {
          this.setShoppingCartToken(this.shoppingCartToken);
        }

        return;
      }

      this._shoppingCartLoading = true;
      this.shoppingCartLoadingState = LoadingState.LOADING;
      const generation = this._cartGeneration;
      try {
        const cart = await Checkout.Api.getShoppingCart(apiClient, this.shoppingCartToken, this.getSelectedEvent?.id);

        // A discard overtook this read; applying it would restore the cart behind it.
        if (generation !== this._cartGeneration) {
          return;
        }

        this.shoppingCart = cart;
        this.shoppingCartToken = this.shoppingCart.token;
        this.setShoppingCartToken(this.shoppingCartToken);
        this.shoppingCartLoadingState = LoadingState.DONE;
      } catch (e) {
        if (generation === this._cartGeneration) {
          this.shoppingCartLoadingState = LoadingState.ERROR;
        }

        throw e;
      } finally {
        this._shoppingCartLoading = false;
      }
    },

    async setTicketCount(ticketId: number, count: number) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, item] = Arrays.replaceOrAdd(
        (this.shoppingCart?.items ?? []).map(ShoppingCartItemInput.fromShoppingCartItem),
        item => 'itemId' in item && item.itemId == ticketId,
        () => ({ ...ShoppingCartItemInput.getNew(ticketId), count: Math.max(count, 0) }),
      );

      await this.storeShoppingCartItem(item);
    },

    async createPaymentInformation(payload: Omit<CreatePaymentInformationInput, 'shoppingCartToken'> = {}): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this._shoppingCartLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation ($input: CreatePaymentInformationInput!) {
              createPaymentInformation(input: $input) {
                paymentInformation {
                  eventId
                  paymentIntent {
                    clientSecret
                    id
                  }
                  paymentProcessor
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input: {
              ...payload,
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        if (data?.createPaymentInformation?.errors) {
          return { errors: VPErrors.fromGqlCollection(data.createPaymentInformation.errors) };
        }

        return data;
      } finally {
        this._shoppingCartLoading = false;
      }
    },

    async updatePaymentInformation(payload: Omit<UpdatePaymentInformationInput, 'shoppingCartToken'>): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      try {
        this.shoppingCartLoadingState = LoadingState.LOADING;
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation ($input: UpdatePaymentInformationInput!) {
              updatePaymentInformation(input: $input) {
                paymentInformation {
                  eventId
                  paymentIntent {
                    clientSecret
                    id
                  }
                  paymentProcessor
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input: {
              ...payload,
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        if (data?.createPaymentInformation?.errors) {
          return { errors: VPErrors.fromGqlCollection(data.createPaymentInformation.errors) };
        }

        return data;
      } finally {
        this.shoppingCartLoadingState = LoadingState.DONE;
      }
    },

    async acquireLock(): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      if (this.shoppingCartLockLoading == LoadingState.LOADING) {
        return;
      }

      this.shoppingCartLockLoading = LoadingState.LOADING;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation ($input: AcquireLockInput!) {
              acquireLock(input: $input) {
                success
                errors
              }
            }
          `,
          variables: {
            input: {
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        if (!data?.acquireLock?.success) {
          throw new TicketLockingError();
        }

        this.shoppingCartLockLoading = LoadingState.DONE;

        return data;
      } catch (e) {
        this.shoppingCartLockLoading = LoadingState.ERROR;
        throw e;
      }
    },

    async releaseLock(): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this._shoppingCartLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation ($input: ReleaseLockInput!) {
              releaseLock(input: $input) {
                shoppingCart {
                  ...ShoppingCartFields
                }
                errors
              }
            }
            ${SHOPPING_CART_FRAGMENT}
          `,
          variables: {
            input: {
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        if (data?.releaseLock?.shoppingCart) {
          this.shoppingCart = Checkout.ShoppingCart.fromGql(data.releaseLock.shoppingCart);
        }

        return data;
      } finally {
        this._shoppingCartLoading = false;
      }
    },

    async storeShoppingCartItem(item: Checkout.ShoppingCartItemInput.ShoppingCartItemInput) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      try {
        this.shoppingCartLoadingState = LoadingState.LOADING;

        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation updateShoppingCart($input: UpdateShoppingCartInput!) {
              updateShoppingCart(input: $input) {
                clientMutationId
                errors
                status
                shoppingCart {
                  ...ShoppingCartFields
                }
              }
            }
            ${SHOPPING_CART_FRAGMENT}
          `,
          variables: {
            input: {
              changes: {
                eventId: this.getSelectedEvent!.id,
                items: [item],
              },
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        if (data?.updateShoppingCart?.shoppingCart) {
          this.shoppingCart = Checkout.ShoppingCart.fromGql(data.updateShoppingCart.shoppingCart);
        }

        const errors = data?.updateShoppingCart?.errors;
        if (errors?.length) {
          throw VPErrorLegacy.getNew(errors);
        }

        if (data?.updateShoppingCart?.status === 'PASSWORD_REQUIRED') {
          throw new Checkout.ShoppingCartError('PASSWORD_REQUIRED');
        }
      } finally {
        this.shoppingCartLoadingState = LoadingState.DONE;
      }
    },

    // Mints the cart's seats.io hold token before any seat is carted, so the chart can open as
    // a manual session on its first and only initialisation. Minting is idempotent server-side
    // (one token per cart) and never touches the cart's line items; the local short-circuit only
    // saves a round trip when the cart already came back carrying one.
    //
    // Deliberately drives no loading state: the cart is not changing, and flipping
    // shoppingCartLoadingState here would blank the ticket UI while a chart initialises.
    async mintHoldToken(): Promise<string | undefined> {
      if (this.seatsioHoldToken) {
        return this.seatsioHoldToken;
      }

      // The picker mounts well after init()'s loadShoppingCart(), so this is best effort for any
      // earlier caller rather than a guarantee — loadShoppingCart() returns immediately while a
      // load is already in flight, and we then simply decline to mint. It can also reject, which
      // reaches the caller as a failed mint. There is nothing to mint against without a cart, and
      // the mutation's shoppingCartToken is non-null.
      if (!this.shoppingCartToken) {
        await this.loadShoppingCart();
      }

      if (!this.shoppingCartToken) {
        return undefined;
      }

      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      const { data } = await apiClient.mutate<Mutation>({
        mutation: gql`
          mutation MintHoldToken($input: MintHoldTokenInput!) {
            mintHoldToken(input: $input) {
              holdToken
              errors
            }
          }
        `,
        variables: {
          input: {
            shoppingCartToken: this.shoppingCartToken,
          },
        },
      });

      const errors = data?.mintHoldToken?.errors;
      if (errors?.length) {
        throw VPErrorLegacy.getNew(errors);
      }

      // Null for an unseated event — nothing was minted, so there is nothing to store.
      const holdToken = data?.mintHoldToken?.holdToken ?? undefined;
      if (holdToken && this.shoppingCart) {
        this.shoppingCart.seatsioHoldToken = holdToken;
      }

      return holdToken;
    },

    beginHold(label: string) {
      this.pendingHolds.add(label);
    },

    settleHold(labels: string[]) {
      labels.forEach(label => {
        this.pendingHolds.delete(label);
      });
    },

    resetPendingHolds() {
      this.pendingHolds.clear();
    },

    // Removes the given seats from the cart with one sequential mutation each.
    // Never route these through the picker's shared `useDebounceFn`, which would
    // coalesce a batch down to a single (last) removal.
    async removeHeldSeats(seats: { seatId: string; blueprintId: number }[]) {
      for (const { seatId, blueprintId } of seats) {
        const item = ShoppingCartItemInput.newSeatTicket(blueprintId);
        item.count = 0;
        item.details = { seatId };
        await this.storeShoppingCartItem(item);
      }
    },

    async clearHeldSeats() {
      const seats = this.getCartedSeatTickets.flatMap(ticket =>
        (ticket.seatingInfo?.seatIds ?? []).map(seatId => ({ seatId, blueprintId: ticket.itemId })),
      );

      // Keep the settle-gate engaged while the cart is being invalidated so the
      // buyer can't advance to payment mid-cleanup (token expiry usually fires
      // with no hold already pending).
      seats.forEach(({ seatId }) => {
        this.pendingHolds.add(seatId);
      });

      try {
        await this.removeHeldSeats(seats);
      } finally {
        seats.forEach(({ seatId }) => {
          this.pendingHolds.delete(seatId);
        });
      }
    },

    async saveOrder(payload: Omit<SaveOrderInput, 'shoppingCartToken'>): Promise<OrderSummaryReponse> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this._shoppingCartLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation SaveOrder ($input: SaveOrderInput!) {
              saveOrder(input: $input) {
                clientMutationId
                errorCode
                errors {
                  path
                  message
                }
                orderSummary {
                  ...OrderSummaryFields
                }
                changeToken {
                  token
                  expiresAt
                }
                success
              }
            }
            ${Checkout.Api.ORDER_SUMMARY_FIELDS}
          `,
          variables: {
            input: {
              ...payload,
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        const result: OrderSummaryReponse = {};

        if (data?.saveOrder?.orderSummary) {
          this.orderSummary = CheckoutOrderSummary.fromGql(data?.saveOrder?.orderSummary);
          result.orderSummary = this.orderSummary;
        }
        if (data?.saveOrder?.errors) {
          result.errors = VPErrors.fromGqlCollection(data.saveOrder.errors);
        }
        if (data?.saveOrder?.errorCode) {
          result.errorCode = data.saveOrder.errorCode;
        }

        return result;
      } finally {
        this._shoppingCartLoading = false;
      }
    },

    async completeOrder(payload: CompleteOrderInput): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this._shoppingCartLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation CompleteOrder ($input: CompleteOrderInput!) {
              completeOrder(input: $input) {
                clientMutationId
                errorCode
                errors {
                  path
                  message
                }
                orderSummary {
                  ...OrderSummaryFields
                }
                changeToken {
                  token
                  expiresAt
                }
                success
              }
            }
            ${Checkout.Api.ORDER_SUMMARY_FIELDS}
          `,
          variables: {
            input: {
              ...payload,
            },
          },
        });

        if (data?.completeOrder?.orderSummary) {
          this.orderSummary = CheckoutOrderSummary.fromGql(data?.completeOrder?.orderSummary);
        }

        return data;
      } finally {
        this._shoppingCartLoading = false;
      }
    },

    async recoverOrder(orderFingerprint: string, paymentIntentId: string): Promise<RecoverOrderResponse> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this._shoppingCartLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation RecoverOrder ($input: RecoverOrderInput!) {
              recoverOrder(input: $input) {
                success
                errorCode
                errors {
                  path
                  message
                }
                orderSummary {
                  ...OrderSummaryFields
                }
              }
            }
            ${Checkout.Api.ORDER_SUMMARY_FIELDS}
          `,
          variables: {
            input: { orderFingerprint, paymentIntentId } as RecoverOrderInput,
          },
        });

        const payload = data?.recoverOrder;

        if (payload?.orderSummary) {
          this.orderSummary = CheckoutOrderSummary.fromGql(payload.orderSummary);
        }

        return {
          success: payload?.success ?? false,
          errorCode: payload?.errorCode ?? undefined,
          orderSummary: payload?.orderSummary ? this.orderSummary : undefined,
          errors: payload?.errors ? VPErrors.fromGqlCollection(payload.errors) : undefined,
        };
      } finally {
        this._shoppingCartLoading = false;
      }
    },

    async updateOrderEmail(fingerprint: string, changeToken: string, email: string): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      const { data } = await apiClient.mutate({
        mutation: gql`
          mutation ($input: UpdateOrderEmailMutationInput!) {
            updateOrderEmail(input: $input) {
              success
              orderSummary {
                ...OrderSummaryFields
              }
            }
          }
          ${Checkout.Api.ORDER_SUMMARY_FIELDS}
        `,
        variables: {
          input: {
            fingerprint,
            changeToken,
            email,
          },
        },
      });

      if (data?.updateOrderEmail?.success) {
        this.orderSummary = CheckoutOrderSummary.fromGql(data?.updateOrderEmail?.orderSummary);
      }
    },

    async loadOrderSummary(fingerprint: string, options: { printTickets?: boolean } = {}) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      if (this.orderSummaryLoading == LoadingState.LOADING) {
        // TODO: wait until finished loading and check if the required event is loaded
        return;
      }

      const query = options.printTickets ? GET_ORDER_SUMMARY_PRINT_TICKETS : GET_ORDER_SUMMARY;

      try {
        this.orderSummaryLoading = LoadingState.LOADING;
        const { data } = await apiClient.query<Query>({
          query,
          fetchPolicy: 'network-only',
          variables: {
            fingerprint,
          },
        });
        if (!data.orderSummary) {
          throw new Error(`No order summary for ${fingerprint}`);
        }
        this.orderSummary = CheckoutOrderSummary.fromGql(data.orderSummary);
        this.orderSummaryLoading = LoadingState.DONE;
      } catch (e) {
        this.orderSummaryLoading = LoadingState.ERROR;
        throw e;
      }
    },
  },
});
